/**
 * Editor→runtime handoff via postMessage. Phase 4b.
 *
 * The protocol:
 *   1. Editor opens the runtime app in a new window.
 *   2. Runtime mounts, posts `branchworks:ready` to its opener.
 *   3. This editor-side listener receives that message and posts back
 *      `branchworks:scenario` with the current draft scenario.
 *   4. Runtime swaps in the scenario and restarts its playthrough.
 *
 * Why postMessage and not URL or localStorage:
 *   - URL parameter caps out around 2-8KB depending on the browser; real
 *     scenarios with media URLs and conditional logic blow past that.
 *   - localStorage requires same-origin between editor and runtime. In dev
 *     they sit on different ports (different origins); in production they
 *     might share an origin but we don't want to bake in that assumption.
 *   - postMessage has no size limit and works cross-origin with the right
 *     targetOrigin discipline.
 *
 * Security note: dev uses `'*'` as targetOrigin and accepts any origin on
 * the receiving side. Production should tighten both to the known runtime
 * origin. A `runtimeOrigin` config parameter lands when Phase 5 introduces
 * deploy-time settings.
 */
import { BW_RUNTIME_URL } from '../buildEnv';
import type { Scenario } from '@branchworks/shared';

/**
 * Where the runtime app is served. In dev it's the Vite runtime server on
 * :5174; in production it's the deployed runtime URL, supplied at build time
 * via VITE_RUNTIME_URL (e.g. https://branchworks-runtime.pages.dev). Falls
 * back to the dev port when the env var isn't set, so `pnpm dev` needs no
 * configuration.
 */
const DEFAULT_RUNTIME_URL = BW_RUNTIME_URL;

/**
 * Open the runtime in a new window and send it the given scenario for preview.
 *
 * The function returns once the new window is opened (synchronously). The
 * handshake completes asynchronously; if the runtime never responds within
 * the timeout, the listener is removed and the new window simply renders its
 * fallback `initialScenario`.
 *
 * `meta.mode` lets the caller distinguish previewing a draft (default) from
 * previewing a specific published version. `meta.versionTag` is shown by the
 * runtime header as "PREVIEW · <tag>" so authors can see which version they're
 * looking at when they're switching between several.
 */
export function launchPreview(
  scenario: Scenario,
  runtimeUrl: string = DEFAULT_RUNTIME_URL,
  meta: { mode?: 'preview' | 'published'; versionTag?: string } = {},
): void {
  const win = window.open(runtimeUrl, '_blank');
  if (!win) {
    // Popup blocker, or the user denied the prompt. We can't recover from
    // here; tell them what happened.
    alert(
      'Could not open the preview window. Check your browser\'s popup blocker for this site.',
    );
    return;
  }

  // Wait for the runtime to signal readiness, then post the scenario.
  // We accept the message from any origin in dev (see security note above).
  const onMessage = (event: MessageEvent) => {
    if (event.source !== win) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.type !== 'branchworks:ready') return;
    win.postMessage(
      {
        type: 'branchworks:scenario',
        scenario,
        mode: meta.mode || 'preview',
        versionTag: meta.versionTag,
      },
      '*', // TODO: tighten to runtime origin in production
    );
    window.removeEventListener('message', onMessage);
    clearTimeout(timeoutId);
  };
  window.addEventListener('message', onMessage);

  // Listener leak prevention: if the runtime never responds (e.g., user
  // closed the window before it loaded), clean up after 10s.
  const timeoutId = window.setTimeout(() => {
    window.removeEventListener('message', onMessage);
  }, 10000);
}
